// The studio's data access, and nothing outside this folder uses it.
//
// One exception, and it is the point of the module boundary: saved drafts live
// in `lib/api.ts`, because `content_drafts` is the seam Compose reads and the
// studio does not own it (docs/AI_STUDIO_PLAN.md §2).

import { supabase, callFunction, FunctionError } from "../lib/supabase";
import { asAppError, PaywallError } from "../lib/entitlements";
import type {
  AiCreditCost, AiGeneration, AiKeyStatus, AiModel, AiSkill, AiThread, DraftIntent,
  GenerationResult,
} from "../lib/types";
import type { PaywallDetail } from "../lib/types";

// ---------------------------------------------------------------- reference

/** The price menu. The same rows the trigger charges against, so they cannot disagree. */
export async function loadPricing(): Promise<{ models: AiModel[]; costs: AiCreditCost[] }> {
  const [models, costs] = await Promise.all([
    supabase.from("ai_models").select("key, display_name, is_premium, sort_order").order("sort_order"),
    supabase.from("ai_credit_costs").select("model_key, intent, credits"),
  ]);
  if (models.error) throw asAppError(models.error);
  if (costs.error) throw asAppError(costs.error);
  return { models: models.data as AiModel[], costs: costs.data as AiCreditCost[] };
}

// ---------------------------------------------------------------- threads

export async function listThreads(workspaceId: string): Promise<AiThread[]> {
  const { data, error } = await supabase
    .from("ai_threads")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("updated_at", { ascending: false })
    .limit(40);
  if (error) throw asAppError(error);
  return data as AiThread[];
}

/** One conversation, oldest first, the way it is read. */
export async function listTurns(threadId: string): Promise<AiGeneration[]> {
  const { data, error } = await supabase
    .from("ai_generations")
    .select("id, thread_id, intent, status, billing, credits, model_key, model_reported, input, output, error, created_at")
    .eq("thread_id", threadId)
    .order("created_at");
  if (error) throw asAppError(error);
  return data as AiGeneration[];
}

export async function deleteThread(threadId: string): Promise<void> {
  const { error } = await supabase.from("ai_threads").delete().eq("id", threadId);
  if (error) throw asAppError(error);
}

// ---------------------------------------------------------------- generate

/**
 * Ask for a draft.
 *
 * The 402 is turned back into a `PaywallError` so the studio can show the same
 * sentence, with the same real numbers, that a refused upload or a refused post
 * already shows. Without this the plan boundary would arrive as a red string
 * and the upgrade link would have nothing to hang on.
 */
export async function generate(args: {
  workspaceId: string;
  threadId: string | null;
  intent: DraftIntent;
  brief: string;
  modelKey: string | null;
  mediaNote?: string | null;
}): Promise<GenerationResult> {
  try {
    return await callFunction<GenerationResult>("ai-generate", args);
  } catch (e) {
    if (e instanceof FunctionError && e.code === "PT402") {
      let detail: PaywallDetail | null = null;
      try {
        detail = e.details ? (JSON.parse(e.details) as PaywallDetail) : null;
      } catch {
        // The message alone still names the limit.
      }
      throw new PaywallError(e.message, detail);
    }
    throw e;
  }
}

// ---------------------------------------------------------------- knowledge

export async function listSkills(workspaceId: string): Promise<AiSkill[]> {
  const { data, error } = await supabase
    .from("ai_skills")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("updated_at", { ascending: false });
  if (error) throw asAppError(error);
  return data as AiSkill[];
}

export async function saveSkill(args: {
  id?: string;
  workspaceId: string;
  name: string;
  body: string;
  appliesTo: "all" | DraftIntent;
  isEnabled: boolean;
}): Promise<AiSkill> {
  const row = {
    workspace_id: args.workspaceId,
    name: args.name.trim(),
    body_md: args.body,
    applies_to: args.appliesTo,
    is_enabled: args.isEnabled,
  };

  const q = args.id
    ? supabase.from("ai_skills").update(row).eq("id", args.id).select("*").single()
    : supabase.from("ai_skills").insert(row).select("*").single();

  const { data, error } = await q;
  if (error) throw asAppError(error);
  return data as AiSkill;
}

export async function deleteSkill(id: string): Promise<void> {
  const { error } = await supabase.from("ai_skills").delete().eq("id", id);
  if (error) throw asAppError(error);
}

// ---------------------------------------------------------------- own key

/** Everything about the key except the key: the view exposes no ciphertext. */
export async function loadKeyStatus(workspaceId: string): Promise<AiKeyStatus | null> {
  const { data, error } = await supabase
    .from("workspace_ai_key_status")
    .select("*")
    .eq("workspace_id", workspaceId)
    .maybeSingle();
  if (error) throw asAppError(error);
  return (data as AiKeyStatus) ?? null;
}

/**
 * The key goes to the Edge Function and never to PostgREST — it is encrypted
 * with a secret that exists only there (ADR-0006). The function checks it
 * against the provider before storing it.
 */
export async function saveKey(args: {
  workspaceId: string;
  provider: "anthropic" | "openai";
  apiKey: string;
}): Promise<{ status: string; last4: string }> {
  return callFunction("ai-key", args);
}

export async function deleteKey(workspaceId: string): Promise<void> {
  const { error } = await supabase.rpc("delete_workspace_ai_key", {
    p_workspace_id: workspaceId,
  });
  if (error) throw asAppError(error);
}

/**
 * Is this workspace drafting on its own key right now?
 *
 * Never throws: a workspace whose key status cannot be read is simply treated as
 * being on plan credits, which is the more conservative of the two answers — it
 * shows a limit rather than hiding one.
 */
export async function listKeyStatusSafe(workspaceId: string): Promise<boolean> {
  try {
    const status = await loadKeyStatus(workspaceId);
    return status?.status === "active";
  } catch {
    return false;
  }
}
